import React, { useState } from 'react'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { Row, Col } from 'reactstrap'
import FieldsetItem from 'components/FieldsetItem'
import { siteHasActiveK8License } from 'lib/siteHasActiveK8License'
import { AssignmentType, Site, SiteUser, Skus, NetSuiteAsset } from 'types'
import { IsRosteredBadge } from 'components/IsRosteredBadge/IsRosteredBadge'
import { HistoricalReport } from 'store/reports/reducer'
import K8ReportDownloadMenu from './K8ReportDownloadMenu'
import {
  LicenseHeader,
  LicenseId,
  LicenseInfo,
  LicenseList,
  LicenseMetaItem,
  LicenseMetaLabel,
  LicenseMetaValue,
  LicenseName,
  LicensesSection,
  LicenseRow,
  LicenseSectionHeader,
  ReportLinkWrapper,
  SectionDivider,
  StyledButton,
} from './component.styles'
import SitePreferencesModal from './SitePreferencesModal'

interface SiteSummaryProps {
  accessCodes?: string[]
  handleClearSiteCache: () => void
  historicalReports?: HistoricalReport[]
  isFetchingHistoricalReports?: boolean
  isFetchingReports: Record<string, boolean>
  onFetchLessonProgressReport: (siteId: number, skuCode: string) => void
  onFetchTrainingReport: (
    siteUserEmails: string[],
    skuCode: string,
    siteName: string,
  ) => void
  reports: Record<string, any>
  site: Site
  siteUsers?: SiteUser[]
  siteNetSuiteAssets?: NetSuiteAsset[]
}

const SiteSummary: React.FC<SiteSummaryProps> = ({
  accessCodes,
  handleClearSiteCache,
  historicalReports = [],
  isFetchingHistoricalReports = false,
  isFetchingReports,
  onFetchLessonProgressReport,
  reports,
  site,
  siteNetSuiteAssets = [],
}) => {
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false)

  const {
    createdDate,
    id,
    isRostered,
    modifiedDate,
    name,
    netSuiteCustomer,
    organization,
    preferences = [],
  } = site
  const { nsCustomerId } = netSuiteCustomer || {}
  const { id: orgId, name: orgName } = organization || {}
  const sitePreferences = preferences.filter(
    preference => preference.assignmentType !== AssignmentType.SiteUser,
  )

  const siteHasK8License = siteHasActiveK8License(site)

  const handleK8ReportClick = () => {
    onFetchLessonProgressReport(id, Skus.K8SkuCode)
  }

  const formatExpirationDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-us', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  return (
    <>
      <fieldset>
        <legend>Details</legend>
        <Row>
          <Col sm={4}>
            <FieldsetItem data={nsCustomerId} title="Site NS Customer Id" />
            <FieldsetItem data={orgId} title="SecondStep Organization Id" />
            <StyledButton color="primary" onClick={handleClearSiteCache}>
              Clear Site Cache
            </StyledButton>
          </Col>
          <Col sm={5}>
            <FieldsetItem data={name} title="Site Name" />
            <FieldsetItem data={orgName} title="SecondStep Organization Name" />
            {accessCodes && accessCodes.length > 0 && (
              <FieldsetItem
                data={accessCodes.join(', ')}
                title="Family Access Codes"
              />
            )}
          </Col>
          <Col sm={3}>
            <FieldsetItem
              color="none"
              data={createdDate ? moment(createdDate).format('YYYY-MM-DD') : ''}
              title="Created Date"
            />
            <FieldsetItem
              color="none"
              data={
                modifiedDate ? moment(modifiedDate).format('YYYY-MM-DD') : ''
              }
              title="Modified Date"
            />
            <FieldsetItem color="none" title="Is Rostered">
              <IsRosteredBadge isRostered={isRostered} summaryView={true} />
            </FieldsetItem>
            <StyledButton
              color="primary"
              onClick={() => setIsPreferencesModalOpen(true)}
            >
              Display Preferences
            </StyledButton>
          </Col>
        </Row>
      </fieldset>
      <SectionDivider />
      <LicensesSection>
        <LicenseSectionHeader>
          <LicenseHeader>Licenses</LicenseHeader>
          {siteHasK8License && (
            <ReportLinkWrapper>
              <K8ReportDownloadMenu
                historicalReports={historicalReports}
                isFetchingCurrentReport={!!isFetchingReports[Skus.K8SkuCode]}
                isFetchingHistoricalReports={isFetchingHistoricalReports}
                onFetchCurrentYearReport={handleK8ReportClick}
                reportUrl={reports[Skus.K8SkuCode] as string | undefined}
                siteId={id}
              />
            </ReportLinkWrapper>
          )}
        </LicenseSectionHeader>
        {siteNetSuiteAssets.length > 0 ? (
          <LicenseList>
            {siteNetSuiteAssets.map(netSuiteAsset => (
              <LicenseRow key={netSuiteAsset.id}>
                <LicenseInfo>
                  <LicenseName>
                    <Link to={`/css-admin/licenses/${netSuiteAsset.id}`}>
                      {netSuiteAsset.product.description}
                    </Link>
                  </LicenseName>
                  <LicenseId>{`License ID: ${netSuiteAsset.nsName}`}</LicenseId>
                </LicenseInfo>
                <LicenseMetaItem>
                  <LicenseMetaLabel>Starts</LicenseMetaLabel>
                  <LicenseMetaValue>
                    {formatExpirationDate(netSuiteAsset.originationDate)}
                  </LicenseMetaValue>
                </LicenseMetaItem>
                <LicenseMetaItem>
                  <LicenseMetaLabel>Expires</LicenseMetaLabel>
                  <LicenseMetaValue>
                    {formatExpirationDate(netSuiteAsset.expirationDate) ||
                      formatExpirationDate(netSuiteAsset.cancelledDate)}
                  </LicenseMetaValue>
                </LicenseMetaItem>
                <LicenseMetaItem>
                  <LicenseMetaLabel>Active</LicenseMetaLabel>
                  <LicenseMetaValue>
                    {netSuiteAsset.isActive ? '✅' : '❌'}
                  </LicenseMetaValue>
                </LicenseMetaItem>
              </LicenseRow>
            ))}
          </LicenseList>
        ) : (
          <div>Site Has No Licenses</div>
        )}
      </LicensesSection>
      <SitePreferencesModal
        isOpen={isPreferencesModalOpen}
        preferences={sitePreferences}
        toggle={() => setIsPreferencesModalOpen(false)}
      />
    </>
  )
}

export default SiteSummary
